import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import * as deepDiff from 'deep-diff';
import {
  Card,
  CardContent,
  CardHeader,
  Link,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { useFieldAuditLog } from 'views/App/Portal/PerformanceReporting/FieldBank/_data_sources/useFieldAuditLog';
import { FieldAuditLog } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import datetimeHelper from 'helpers/datetimeHelper';
import GridSection from 'components/_layouts/GridSection';
import Modal from 'components/_layouts/Modal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with UTC support
dayjs.extend(utc);

interface ChangeDetailRow {
  id: string;
  attribute: string;
  previousValue: string;
  newValue: string;
}

export default function ChangeLog() {
  const { id } = useParams();
  const fieldId = Number(id);
  const auditLogQuery = useFieldAuditLog(fieldId);
  const [selectedAuditLog, setSelectedAuditLog] = useState<FieldAuditLog | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (auditLog: FieldAuditLog) => {
    setSelectedAuditLog(auditLog);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAuditLog(null);
  };

  const getActionText = (operationType: FieldAuditLog['operationType'], previousState: any, newState: any) => {
    switch (operationType) {
      case 'Create':
        return newState.status === 'Active' ? 'Published' : 'Created';
      case 'Update':
        if (previousState?.status === 'Draft' && newState.status === 'Active') {
          return 'Published';
        } else if (previousState?.status === 'Active' && newState.status === 'Inactive') {
          return 'Deactivated';
        }
        return 'Edited';
      case 'Deactivate':
        return 'Deactivated';
      default:
        return operationType;
    }
  };

  const getFieldChanges = (auditLog: FieldAuditLog): ChangeDetailRow[] | null => {
    const { previousState, newState, operationType } = auditLog;

    if (!newState) {
      return null;
    }

    const changes: ChangeDetailRow[] = [];

    // Helper function to get human-readable value
    const getDisplayValue = (value: any, field: string): string => {
      if (value === null || value === undefined) return 'N/A';
      if (field === 'fieldCategory' && typeof value === 'object') {
        return value.name || 'N/A';
      }
      if (field === 'status') {
        return value;
      }
      if (field === 'fieldType') {
        return t(`performanceReporting.fieldBank.fieldType.${value}`) || value;
      }
      if (field === 'isEisScorable') {
        // For boolean fields, show True/False
        return value === true ? 'True' : 'False';
      }
      if (field === 'defaultValue') {
        // Format default value based on field type
        const fieldType = newState?.fieldType || previousState?.fieldType;
        if (fieldType === 'Time') {
          // For time fields, show only the time portion in user's local timezone
          const timeValue = dayjs.utc(value).local();
          return timeValue.isValid() ? timeValue.format('HH:mm') : String(value);
        }
        if (fieldType === 'Date') {
          // Parse UTC ISO string and convert to local time for display
          // This ensures dates display correctly in the user's timezone
          const dateValue = dayjs.utc(value).local();
          return dateValue.isValid() ? dateValue.format('MM/DD/YYYY') : String(value);
        }
        if (fieldType === 'Boolean') {
          // For boolean fields, show True/False instead of true/false strings
          if (value === 'true') return 'True';
          if (value === 'false') return 'False';
        }
      }
      return String(value);
    };

    // Define fields to check for changes
    const fieldsToCheck = [
      { key: 'label', name: 'Label' },
      { key: 'description', name: 'Description' },
      { key: 'defaultValue', name: 'Default Value' },
      { key: 'fieldType', name: 'Data Type' },
      { key: 'status', name: 'Status' },
      { key: 'fieldCategory', name: 'Category' },
      { key: 'isEisScorable', name: 'EIS Scorable' },
    ];

    // For Create operations, show all fields as new values
    if (operationType === 'Create' && !previousState && newState) {
      fieldsToCheck.forEach((field) => {
        const newValue = (newState as any)?.[field.key];
        if (newValue !== null && newValue !== undefined) {
          changes.push({
            id: field.key,
            attribute: field.name,
            previousValue: 'N/A',
            newValue: getDisplayValue(newValue, field.key),
          });
        }
      });
    } else {
      // For Update operations, use deep-diff to compare
      const differences = deepDiff.diff(previousState, newState);

      if (differences) {
        const fieldMap = new Map(fieldsToCheck.map((f) => [f.key, f.name]));

        differences.forEach((change: deepDiff.Diff<any, any>) => {
          if ((change.kind === 'E' || change.kind === 'N' || change.kind === 'D') && change.path) {
            // E: Edit (value changed), N: New (value added), D: Deleted (value removed)
            const fieldPath = change.path.join('.');
            let fieldKey = fieldPath;

            // Handle nested field paths (e.g., 'fieldCategory.name' -> 'fieldCategory')
            if (fieldPath === 'fieldCategory.name') {
              fieldKey = 'fieldCategory';
            }

            // Only include fields we care about
            if (fieldMap.has(fieldKey)) {
              const fieldName = fieldMap.get(fieldKey)!;

              // Get the appropriate values based on the field type
              let previousValue, newValue;
              if (fieldKey === 'fieldCategory' && fieldPath === 'fieldCategory.name') {
                // For nested category name changes, show the category names
                previousValue = change.kind === 'N' ? null : change.lhs;
                newValue = change.kind === 'D' ? null : change.rhs;
              } else {
                // For direct field changes
                previousValue = (previousState as any)?.[fieldKey];
                newValue = (newState as any)?.[fieldKey];
              }

              changes.push({
                id: fieldKey,
                attribute: fieldName,
                previousValue: getDisplayValue(previousValue, fieldKey),
                newValue: getDisplayValue(newValue, fieldKey),
              });
            }
          }
        });
      }
    }

    return changes;
  };

  if (auditLogQuery.isLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (auditLogQuery.isError) {
    return <Alert severity='error'>{t('performanceReporting.fieldBank.changeLog.errorLoading')}</Alert>;
  }

  // Flatten all pages for infinite query
  const auditLogs = auditLogQuery.data?.pages.flatMap((page) => page.content) || [];
  const filteredAuditLogs = auditLogs;
  const totalCount = auditLogQuery.data?.pages[0]?.count || 0;

  const columns = {
    timestamp: {
      header: t('performanceReporting.fieldBank.changeLog.columns.dateTime'),
      flex: 1,
      minWidth: 180,
      renderer: (params: any) => (
        <Typography variant='body2'>{datetimeHelper.formatDateTime(params.row.timestamp)}</Typography>
      ),
    },
    operationType: {
      header: t('performanceReporting.fieldBank.changeLog.columns.action'),
      flex: 1,
      minWidth: 120,
      renderer: (params: any) => {
        const auditLog = params.row as FieldAuditLog;
        return (
          <Typography variant='body2'>
            {getActionText(auditLog.operationType, auditLog.previousState, auditLog.newState)}
          </Typography>
        );
      },
    },
    actor: {
      header: t('performanceReporting.fieldBank.changeLog.columns.author'),
      flex: 1,
      minWidth: 200,
      renderer: (params: any) => <Typography variant='body2'>{params.row.actor}</Typography>,
    },
    changeNote: {
      header: t('performanceReporting.fieldBank.changeLog.columns.note'),
      flex: 2,
      minWidth: 250,
      renderer: (params: any) => (
        <Typography
          variant='body2'
          sx={{
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.4,
            py: 1,
            maxHeight: '80px',
            overflow: 'auto',
          }}
        >
          {params.row.changeNote || '--'}
        </Typography>
      ),
    },
    viewDetails: {
      header: t('performanceReporting.fieldBank.changeLog.columns.viewDetails'),
      flex: 0,
      minWidth: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderer: (params: any) => {
        const auditLog = params.row as FieldAuditLog;
        const hasParseError = !auditLog.newState;
        const isCreateAsDraft = auditLog.operationType === 'Create' && auditLog.newState?.status === 'Draft';
        const isDisabled = hasParseError || isCreateAsDraft;

        let tooltipTitle = '';
        if (hasParseError) {
          tooltipTitle = 'Unable to parse change details';
        } else if (isCreateAsDraft) {
          tooltipTitle = 'No changes to view for draft creation';
        }

        return (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            height='100%'
          >
            <Tooltip
              title={tooltipTitle}
              arrow
            >
              <span>
                <IconButton
                  size='small'
                  onClick={() => handleViewDetails(params.row)}
                  sx={{ color: 'primary.main' }}
                  disabled={isDisabled}
                >
                  <DescriptionIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  };

  return (
    <Stack spacing={3}>
      <Card className='lex-card'>
        <CardHeader title={t('performanceReporting.fieldBank.changeLog.title')} />
        <CardContent>
          {filteredAuditLogs.length === 0 ? (
            <Typography
              variant='body2'
              color='text.secondary'
            >
              {t('performanceReporting.fieldBank.changeLog.noChanges')}
            </Typography>
          ) : (
            <GridSection
              data={filteredAuditLogs}
              columns={columns}
              totalCount={totalCount}
              gridProps={{
                autoHeight: true,
                disableColumnFilter: true,
                disableColumnMenu: true,
                getRowId: (row: any) => row.id,
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        title={t('performanceReporting.fieldBank.changeLog.detailsModal.title')}
        subtitle={t('performanceReporting.fieldBank.changeLog.detailsModal.viewDetails')}
        size='md'
      >
        {selectedAuditLog &&
          (() => {
            const changes = getFieldChanges(selectedAuditLog);

            if (changes === null) {
              return (
                <Alert severity='error'>{t('performanceReporting.fieldBank.changeLog.detailsModal.parseError')}</Alert>
              );
            }

            return (
              <Box>
                <Typography
                  variant='body1'
                  sx={{ mb: 3, color: 'text.secondary' }}
                >
                  {t('performanceReporting.fieldBank.changeLog.detailsModal.subtitle').replace(
                    '{count}',
                    changes.length.toString()
                  )}
                </Typography>

                <GridSection
                  data={changes}
                  columns={{
                    attribute: {
                      header: t('performanceReporting.fieldBank.changeLog.detailsModal.columns.attribute'),
                      flex: 1,
                      minWidth: 120,
                      renderer: (params: any) => (
                        <Typography
                          variant='body2'
                          sx={{ fontWeight: 500 }}
                        >
                          {params.row.attribute}
                        </Typography>
                      ),
                    },
                    previousValue: {
                      header: t('performanceReporting.fieldBank.changeLog.detailsModal.columns.previousValue'),
                      flex: 1,
                      minWidth: 150,
                      renderer: (params: any) => (
                        <Typography
                          variant='body2'
                          sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            maxHeight: '100px',
                            overflow: 'auto',

                            py: 4,
                          }}
                        >
                          {params.row.previousValue}
                        </Typography>
                      ),
                    },
                    newValue: {
                      header: t('performanceReporting.fieldBank.changeLog.detailsModal.columns.newValue'),
                      flex: 1,
                      minWidth: 150,
                      renderer: (params: any) => (
                        <Typography
                          variant='body2'
                          sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            maxHeight: '100px',
                            overflow: 'auto',

                            py: 4,
                          }}
                        >
                          {params.row.newValue}
                        </Typography>
                      ),
                    },
                  }}
                  gridProps={{
                    autoHeight: true,
                    disableColumnFilter: true,
                    disableColumnMenu: true,
                    hideFooter: true,
                    getRowId: (row: any) => row.id,
                  }}
                />
              </Box>
            );
          })()}
      </Modal>
    </Stack>
  );
}
